import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { clearBdCache, useBdApiClient, useBdApiContext, useBdQuery } from "../../hooks/useBdApi";
import { currentEtYear } from "../../hooks/usePeriod";

const CURRENT_YEAR = currentEtYear();

// Year selection lives in the URL (?year=2026) so AGP views are shareable.
export function useAgpYear() {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = parseInt(searchParams.get("year"), 10);
  const year = Number.isFinite(raw) && raw >= 2020 && raw <= 2100 ? raw : CURRENT_YEAR;
  const setYear = useCallback(
    (y) => {
      const next = new URLSearchParams(searchParams);
      if (!y || Number(y) === CURRENT_YEAR) next.delete("year");
      else next.set("year", String(y));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const search = year === CURRENT_YEAR ? "" : `?year=${year}`;
  return { year, setYear, search, isCurrentYear: year === CURRENT_YEAR };
}

export function useAgpQuery(path, params) {
  return useBdQuery(`agp/${path}`, params);
}

// Mutations. Every successful write clears the cache and bumps the data
// version so all open AGP views refetch.
export function useAgpMutations() {
  const api = useBdApiClient();
  const { bump } = useBdApiContext();

  const run = useCallback(
    async (promise, okMessage) => {
      const res = await promise;
      if (!res || res.success === false) {
        const message = (res && (res.error || res.message)) || "Request failed";
        toast.error(message);
        return { ok: false, error: message };
      }
      clearBdCache();
      bump();
      if (okMessage) toast.success(okMessage);
      return { ok: true, data: res };
    },
    [bump]
  );

  return useMemo(
    () => ({
      putTeam: (teamKey, year, body) => run(api.put(`landmark/bd/agp/teams/${teamKey}?year=${year}`, body), "Team goals saved"),
      putMember: (sfUserId, year, teamKey, body) =>
        run(api.put(`landmark/bd/agp/members/${sfUserId}?year=${year}&teamKey=${teamKey}`, body), "Goals saved"),
      addMember: (teamKey, year, sfUserId) => run(api.post(`landmark/bd/agp/teams/${teamKey}/members?year=${year}`, { sfUserId }), "Member added"),
      removeMember: (teamKey, year, sfUserId) =>
        run(api.delete(`landmark/bd/agp/teams/${teamKey}/members/${sfUserId}?year=${year}`), "Member removed"),
      addActual: (body) => run(api.post("landmark/bd/agp/actuals", body), "Actual recorded"),
      deleteActual: (entryId) => run(api.delete(`landmark/bd/agp/actuals/${entryId}`), "Entry removed"),
      copyYear: (fromYear, toYear) => run(api.post(`landmark/bd/agp/copy?fromYear=${fromYear}&toYear=${toYear}`, {}), `Copied ${fromYear} goals to ${toYear}`),
    }),
    [api, run]
  );
}
